import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, EMPTY, finalize, map, of, switchMap, tap } from 'rxjs';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { AclpermissionActions } from '../../actions/generations/aclpermission.actions';

@Injectable()
export class AclpermissionEffects {
  private readonly actions = inject(Actions);
  private readonly httpService = inject(HttpBaseService);
  private readonly loaderService = inject(AppLoaderService);

  getAllAclPermissions$ = createEffect(() =>
    this.actions.pipe(
      ofType(AclpermissionActions.getAllAclPermissions),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, payloadWithPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, payloadWithPathVariables).pipe(
          map((response: any) => AclpermissionActions.setAclPermissions({ aclPermissions: response.results || [] })),
          catchError(() => EMPTY),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  createAclPermission$ = createEffect(() =>
    this.actions.pipe(
      ofType(AclpermissionActions.createAclPermission),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            return AclpermissionActions.createAclPermissionSuccess({ aclPermission: response });
          }),
          catchError(() => {
            return of(AclpermissionActions.createAclPermissionFailure());
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  updateAclPermission$ = createEffect(() =>
    this.actions.pipe(
      ofType(AclpermissionActions.updateAclPermission),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            return AclpermissionActions.updateAclPermissionSuccess({ aclPermission: response });
          }),
          catchError(() => {
            return of(AclpermissionActions.updateAclPermissionFailure());
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );

  batchUpdateAclPermissions$ = createEffect(() =>
    this.actions.pipe(
      ofType(AclpermissionActions.batchUpdateAclPermissions),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, payload } = action;
        return this.httpService[apiConstants.method](apiConstants, payload).pipe(
          map((response: any) => {
            const permissions = Array.isArray(response) ? response : (response?.results || []);
            return AclpermissionActions.batchUpdateAclPermissionsSuccess({ aclPermissions: permissions });
          }),
          catchError(() => {
            return of(AclpermissionActions.batchUpdateAclPermissionsFailure());
          }),
          finalize(() => this.loaderService.hide())
        );
      })
    )
  );
}
